// src/pages/DeviceManagement/components/FilterPanel/ActiveFilters.jsx
// Display and manage active filters as chips
// Part of Device Management Overhaul - Phase 1

import React from 'react';
import { Box, Chip, Typography, Button, Divider } from '@mui/material';
import { Clear as ClearIcon, FilterAlt as FilterIcon } from '@mui/icons-material';

const ActiveFilters = ({ filters, onFilterChange, locations, deviceTypes }) => {
  const activeFilters = [];

  // Quick filters
  if (filters.quickFilters?.size > 0) {
    filters.quickFilters.forEach(filter => {
      activeFilters.push({
        type: 'quick',
        key: `quick-${filter}`,
        label: filter.charAt(0).toUpperCase() + filter.slice(1),
        value: filter,
        color: getQuickFilterColor(filter)
      });
    });
  }

  // Search filter
  if (filters.search) {
    activeFilters.push({
      type: 'search',
      key: 'search',
      label: `Search: "${filters.search}"`,
      value: filters.search,
      color: 'default'
    });
  }

  // Location filters
  if (Array.isArray(filters.locationId) && filters.locationId.length > 0) {
    filters.locationId.forEach(id => {
      const location = locations?.find(l => l.id === id);
      activeFilters.push({
        type: 'location',
        key: `location-${id}`,
        label: `Location: ${location?.friendlyName || id}`,
        value: id,
        color: 'primary'
      });
    });
  }

  // Device type filters
  if (Array.isArray(filters.deviceTypeId) && filters.deviceTypeId.length > 0) {
    filters.deviceTypeId.forEach(id => {
      const deviceType = deviceTypes?.find(dt => dt.id === id);
      activeFilters.push({
        type: 'deviceType',
        key: `type-${id}`,
        label: `Type: ${deviceType?.friendlyName || id}`,
        value: id,
        color: 'secondary'
      });
    });
  }

  // Status filters - handle both string and array
  const statusArray = Array.isArray(filters.status)
    ? filters.status
    : (filters.status ? [filters.status] : []);

  if (statusArray.length > 0) {
    statusArray.forEach(status => {
      activeFilters.push({
        type: 'status',
        key: `status-${status}`,
        label: `Status: ${status}`,
        value: status,
        color: getStatusColor(status)
      });
    });
  }

  // Date range filters
  if (filters.lastSeenAfter) {
    activeFilters.push({
      type: 'dateAfter',
      key: 'date-after',
      label: `After: ${new Date(filters.lastSeenAfter).toLocaleDateString()}`,
      value: filters.lastSeenAfter,
      color: 'default'
    });
  }

  if (filters.lastSeenBefore) {
    activeFilters.push({
      type: 'dateBefore',
      key: 'date-before',
      label: `Before: ${new Date(filters.lastSeenBefore).toLocaleDateString()}`,
      value: filters.lastSeenBefore,
      color: 'default'
    });
  }

  // Unlinked filter
  if (filters.unlinked) {
    activeFilters.push({
      type: 'unlinked',
      key: 'unlinked',
      label: 'Unlinked Devices',
      value: true,
      color: 'warning'
    });
  }

  const removeFilter = (filter) => {
    const newFilters = { ...filters };

    switch (filter.type) {
      case 'quick':
        const newQuickFilters = new Set(filters.quickFilters);
        newQuickFilters.delete(filter.value);
        newFilters.quickFilters = newQuickFilters;
        break;

      case 'search':
        newFilters.search = '';
        break;

      case 'location':
        newFilters.locationId = filters.locationId.filter(id => id !== filter.value);
        break;

      case 'deviceType':
        newFilters.deviceTypeId = filters.deviceTypeId.filter(id => id !== filter.value);
        break;

      case 'status':
        newFilters.status = filters.status.filter(s => s !== filter.value);
        break;

      case 'dateAfter':
        newFilters.lastSeenAfter = null;
        break;

      case 'dateBefore':
        newFilters.lastSeenBefore = null;
        break;

      case 'unlinked':
        newFilters.unlinked = false;
        break;

      default:
        break;
    }

    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    onFilterChange({
      search: '',
      quickFilters: new Set(),
      status: [],
      locationId: [],
      deviceTypeId: [],
      lastSeenAfter: null,
      lastSeenBefore: null,
      unlinked: false
    });
  };

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={500}>
            Active Filters ({activeFilters.length})
          </Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          color="secondary"
          startIcon={<ClearIcon />}
          onClick={clearAllFilters}
          sx={{ textTransform: 'none' }}
        >
          Clear All
        </Button>
      </Box>

      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        p: 1.5,
        backgroundColor: 'action.hover',
        borderRadius: 1
      }}>
        {activeFilters.map((filter) => (
          <Chip
            key={filter.key}
            label={filter.label}
            onDelete={() => removeFilter(filter)}
            color={filter.color}
            size="small"
            sx={{
              maxWidth: '250px',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          />
        ))}
      </Box>

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};

// Helper functions for colors
function getQuickFilterColor(filter) {
  const colorMap = {
    offline: 'error',
    online: 'success',
    unlinked: 'warning',
    recentlySeen: 'info'
  };
  return colorMap[filter] || 'default';
}

function getStatusColor(status) {
  const colorMap = {
    online: 'success',
    offline: 'error'
  };
  return colorMap[status] || 'default';
}

export default ActiveFilters;